import { useEffect, useMemo, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import {
  currentRsvpSummary,
  findAttendanceOptions,
  isPollOpen,
  buildPollRespondPayload,
} from "../../../utils/clientSessionUtils";
import s from "./clientApp.module.css";
import { useToast } from "../../ui";

function sessionIdOf(session) {
  return session?.session_id || session?.id;
}

function isTrainingSession(session) {
  if (!session || session.session_kind === "event") return false;
  return (
    session.source === "training_session" ||
    Boolean(session.session_id || session.id)
  );
}

function responseLabel(summary) {
  if (!summary) return null;
  if (summary.forecast === "coming") {
    return { title: "Буду", tone: "yes" };
  }
  if (summary.forecast === "not_coming") {
    return {
      title: "Не буду",
      tone: "no",
      reason: summary.reason,
    };
  }
  return null;
}

function isCompetitionSession(session) {
  return session?.session_kind === "event" && session?.event_kind === "competition";
}

export default function AttendancePollButtons({
  session,
  pollRow,
  onDone,
  onPhaseChange,
  compact = false,
  variant = "default",
  question = "Придёте на тренировку?",
  allowDirect = true,
}) {
  const toast = useToast();
  const [phase, setPhase] = useState("choose");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const isCompetition = isCompetitionSession(session);

  const { yes, no, all } = findAttendanceOptions(pollRow);
  const hasPoll = pollRow && isPollOpen(pollRow);
  const sid = pollRow?.session_id || pollRow?.session?.id || sessionIdOf(session);
  const canPollApi =
    Boolean(pollRow?.dispatch_id) ||
    (pollRow?.poll_id && sid);
  const usePollFlow =
    hasPoll && canPollApi && (yes || all.length > 0);
  const canDirect = allowDirect && isTrainingSession(session) && sessionIdOf(session);
  const current = useMemo(
    () => currentRsvpSummary(session, pollRow),
    [session, pollRow],
  );
  const answered = Boolean(current);
  const showForm = !answered || editing || phase === "reason";

  useEffect(() => {
    const uiPhase =
      phase === "reason"
        ? "reason"
        : !showForm && answered
          ? "answered"
          : "choose";
    onPhaseChange?.(uiPhase);
  }, [phase, showForm, answered, onPhaseChange]);

  const wrapClass =
    variant === "sheet" ? s.pollSheet : compact ? s.pollCompact : s.pollBlock;

  const submitPoll = async (optionId, reasonText) => {
    const payload = {
      option_id: optionId,
      reason: reasonText || undefined,
    };
    if (pollRow.poll_id) payload.poll_id = pollRow.poll_id;
    const sid = pollRow.session_id || pollRow.session?.id || sessionIdOf(session);
    if (sid) payload.session_id = sid;
    const { routeId, body } = buildPollRespondPayload(pollRow, payload);
    await clinicAPI.respondTrainingPoll(routeId, body);
  };

  const submitDirect = async (coming, reasonText) => {
    await clinicAPI.postSessionRsvp(sessionIdOf(session), {
      coming,
      reason: reasonText || undefined,
    });
  };

  const finish = () => {
    toast.success("Ответ сохранён");
    setPhase("choose");
    setReason("");
    setEditing(false);
    onDone?.();
  };

  const handleYes = async () => {
    if (busy) return;
    setBusy("yes");
    try {
      if (usePollFlow && yes) {
        await submitPoll(yes.id);
      } else if (canDirect) {
        await submitDirect(true);
      } else {
        return;
      }
      finish();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось отправить");
    } finally {
      setBusy(null);
    }
  };

  const handleNoClick = () => {
    setPhase("reason");
    setReason("");
  };

  const handleNoConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      toast.error("Укажите причину");
      return;
    }
    if (busy) return;
    setBusy("no");
    try {
      if (usePollFlow && no) {
        await submitPoll(no.id, trimmed);
      } else if (canDirect) {
        await submitDirect(false, trimmed);
      } else {
        return;
      }
      finish();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось отправить");
    } finally {
      setBusy(null);
    }
  };

  if (isCompetition) return null;

  if (!usePollFlow && !canDirect) return null;

  if (!showForm && answered) {
    const label = responseLabel(current);
    if (!label) return null;
    const canChange = (usePollFlow && isPollOpen(pollRow)) || canDirect;
    return (
      <div className={`${wrapClass} ${variant === "sheet" ? s.pollSheetAnswered : ""}`}>
        <div className={s.rsvpAnswerRow}>
          <span
            className={`${s.rsvpAnswerBadge} ${
              label.tone === "yes" ? s.rsvpAnswerBadgeYes : s.rsvpAnswerBadgeNo
            }`}
          >
            {label.title}
          </span>
          {canChange ? (
            <button
              type="button"
              className={s.rsvpChangeBtn}
              onClick={() => setEditing(true)}
            >
              Изменить
            </button>
          ) : null}
        </div>
        {label.reason ? <p className={s.rsvpAnswerReason}>{label.reason}</p> : null}
      </div>
    );
  }

  if (phase === "reason") {
    return (
      <div className={`${wrapClass} ${variant === "sheet" ? s.pollSheetReason : ""}`}>
        {variant === "sheet" ? (
          <>
            <p className={s.pollSheetReasonTitle}>Причина отсутствия</p>
            <p className={s.pollSheetReasonHint}>
              Кратко опишите, почему не сможете прийти
            </p>
          </>
        ) : (
          <p className={s.pollQuestion}>Почему не сможете прийти?</p>
        )}
        <textarea
          className={s.rsvpReasonInput}
          rows={3}
          placeholder="Например: болею, командировка, экзамены…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
        />
        <div className={s.rsvpReasonActions}>
          <button
            type="button"
            className={s.rsvpBackBtn}
            disabled={!!busy}
            onClick={() => {
              setPhase("choose");
              setReason("");
              if (answered) setEditing(false);
            }}
          >
            Назад
          </button>
          <button
            type="button"
            className={variant === "sheet" ? s.pollBtnSubmit : s.pollBtnYes}
            disabled={!!busy}
            onClick={handleNoConfirm}
          >
            {busy === "no" ? "…" : "Отправить"}
          </button>
        </div>
      </div>
    );
  }

  const yesLabel = "Буду";
  const noLabel = "Не буду";

  if (usePollFlow && yes && no) {
    return (
      <div className={wrapClass}>
        {variant === "sheet" ? (
          <p className={s.pollSheetQuestion}>{question}</p>
        ) : (
          <p className={s.pollQuestion}>{question}</p>
        )}
        <div className={s.pollBtnRow}>
          <button
            type="button"
            className={s.pollBtnYes}
            disabled={!!busy}
            onClick={handleYes}
          >
            {busy === "yes" ? "…" : yesLabel}
          </button>
          <button
            type="button"
            className={s.pollBtnNo}
            disabled={!!busy}
            onClick={handleNoClick}
          >
            {noLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapClass}>
      {variant === "sheet" ? (
        <p className={s.pollSheetQuestion}>{question}</p>
      ) : (
        <p className={s.pollQuestion}>{question}</p>
      )}
      {usePollFlow && all.length > 0 ? (
        <div className={s.pollBtnRow}>
          {all.map((opt) => {
            const isNo =
              opt.forecast === "not_coming" ||
              /не приду|не смогу|не буду/i.test(opt.text || "");
            const isYes =
              !isNo &&
              (opt.forecast === "coming" || /приду|буду/i.test(opt.text || ""));
            return (
              <button
                key={opt.id}
                type="button"
                className={isNo ? s.pollBtnNo : s.pollBtnYes}
                disabled={!!busy}
                onClick={() => {
                  if (isNo) {
                    handleNoClick();
                    return;
                  }
                  if (!isYes || busy) return;
                  setBusy(opt.id);
                  submitPoll(opt.id)
                    .then(finish)
                    .catch((e) =>
                      toast.error(e.response?.data?.error || "Не удалось отправить"),
                    )
                    .finally(() => setBusy(null));
                }}
              >
                {busy === opt.id ? "…" : opt.text}
              </button>
            );
          })}
        </div>
      ) : (
        <div className={s.pollBtnRow}>
          <button
            type="button"
            className={s.pollBtnYes}
            disabled={!!busy}
            onClick={handleYes}
          >
            {busy === "yes" ? "…" : yesLabel}
          </button>
          <button
            type="button"
            className={s.pollBtnNo}
            disabled={!!busy}
            onClick={handleNoClick}
          >
            {noLabel}
          </button>
        </div>
      )}
    </div>
  );
}
